import {Pane} from 'evergreen-ui';
import {memo, useState} from 'react';
import {CurrentUserData, useCurrentUser} from "../../security";
import {hasEumAccess} from "../../usermanagementapp/domain/security";
import {APP_GROUPS, APP_SUBGROUPS, AppDestination} from "../domain/accessMap";
import {useAccessMap} from "../domain/useAccessMap";
import {linkPrefix} from './util';
import {NotificationMenuOption} from "../notification/NotificationMenuOption";
import info from '../../actuator/info.json';
import {useApplicationAuthType, ApplicationRootUrl} from "common/component/ApplicationRootContext";

const clientPlacementUrl = process.env.REACT_APP_CLIENT_PLACEMENT_URL ?? "";
const clientPlacementUrlInternal = process.env.REACT_APP_CLIENT_PLACEMENT_INTERNAL_URL ?? "";
const userManagementEumUrl = process.env.REACT_APP_USER_MANGEMENT_EUM_URL ?? "";

type LeftNavbarProps = {
    currentSelection: 'Client Placement' | 'User Management' | 'Client Care' | 'Authorization' | 'Notification' | 'Client Offers - At Risk' | 'Submitted Requests' | 'Provider Metrics' | 'Anticipated Discharges' | 'Roster Management' | 'Audit Export' | 'Progress Portrait' | 'Contact Management';
};

const NavItem = ({destination, currentSelection, cpBaseUrl}: { destination: AppDestination; currentSelection: string; cpBaseUrl: string }) => (
    <li className={currentSelection === destination.selection ? "portal-layout-navbar-cp" : ''}>
        <a href={cpBaseUrl + destination.route}
           className={destination.key === 'notification' ? "small-padding portal-nav-hover" : "portal-nav-hover"}>
            {destination.key === 'notification'
                ? <NotificationMenuOption/>
                : <><i className={destination.icon}/>{destination.label}</>}
        </a>
    </li>
);

/** Header only - no route, no page. Folds its own children; the group above folds the whole tier. */
const NavSubGroup = ({label, icon, items, currentSelection, cpBaseUrl}: { label: string; icon: string; items: AppDestination[]; currentSelection: string; cpBaseUrl: string }) => {
    const [expanded, setExpanded] = useState(true);

    return (
        <li className="portal-nav-subgroup">
            <button type="button" className="portal-nav-subgroup-header" aria-expanded={expanded}
                    onClick={() => setExpanded((value) => !value)}>
                <i className={icon}/>
                {label}
                <i className={`portal-nav-group-chevron fa-solid ${expanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}/>
            </button>
            {expanded &&
                <ul className="portal-nav-subgroup-children">
                    {items.map((destination) =>
                        <NavItem key={destination.key} destination={destination}
                                 currentSelection={currentSelection} cpBaseUrl={cpBaseUrl}/>
                    )}
                </ul>
            }
        </li>
    );
};

const NavGroup = ({group, currentUser, currentSelection, cpBaseUrl}: { group: typeof APP_GROUPS[number]; currentUser: CurrentUserData | null; currentSelection: string; cpBaseUrl: string }) => {
    const [expanded, setExpanded] = useState(true);
    const byLabel = (a: AppDestination, b: AppDestination) => a.label.localeCompare(b.label);
    const items = useAccessMap()
        .filter((destination) => destination.group === group.key && destination.canAccess(currentUser));

    // Sub-groups first in APP_SUBGROUPS order, then the ungrouped destinations alphabetised.
    const subGroups = APP_SUBGROUPS
        .map((subGroup) => ({
            ...subGroup,
            items: items.filter((destination) => destination.subGroup === subGroup.key).sort(byLabel),
        }))
        .filter((subGroup) => subGroup.items.length > 0);
    const ungrouped = items.filter((destination) => !destination.subGroup).sort(byLabel);

    if (items.length === 0) {
        return null;
    }

    return (
        <li className="portal-nav-group">
            <button type="button" className="portal-nav-group-header" onClick={() => setExpanded((value) => !value)}>
                <i className={group.icon}/>
                {group.label}
                <i className={`portal-nav-group-chevron fa-solid ${expanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}/>
            </button>
            {expanded &&
                <ul className="portal-nav-group-children">
                    {subGroups.map((subGroup) =>
                        <NavSubGroup key={subGroup.key} label={subGroup.label} icon={subGroup.icon}
                                     items={subGroup.items}
                                     currentSelection={currentSelection} cpBaseUrl={cpBaseUrl}/>
                    )}
                    {ungrouped.map((destination) =>
                        <NavItem key={destination.key} destination={destination}
                                 currentSelection={currentSelection} cpBaseUrl={cpBaseUrl}/>
                    )}
                </ul>
            }
        </li>
    );
};

export const LeftNavbar = memo(({currentSelection}: LeftNavbarProps) => {
    const currentUser = useCurrentUser();
    const eumAccess = hasEumAccess(currentUser);
    const feVersion = `v${info["version"]}-${info["commit"]}`;
    const authType = useApplicationAuthType();
    const cpApplicationRoot = `/${authType === 'internal' ? ApplicationRootUrl.AZURE_INTERNAL_CP_PROVIDER_ROUTE_NAME : ApplicationRootUrl.EXTERNAL_PROVIDER_APP_ROOT_URL}`;
    const eumApplicationRoot = `/${authType === 'internal' ? ApplicationRootUrl.AZURE_INTERNAL_UM_PROVIDER_ROUTE_NAME: ApplicationRootUrl.EXTERNAL_EUM_APP_ROOT_URL}`;
    const cpApplicationDomainUrl = authType === 'internal' ? clientPlacementUrlInternal : clientPlacementUrl;
    const umApplicationDomainUrl = authType === 'internal' ? clientPlacementUrlInternal : userManagementEumUrl;
    const cpBaseUrl = cpApplicationDomainUrl + cpApplicationRoot;

    return (
        <Pane className="portal-layout-navbar tracking-wider" display="flex" flexDirection="column" overflowY="scroll">
            <h1>Provider Portal</h1>

            <ul className='leading-4 portal-top-nav portal-nav'>
                {APP_GROUPS.map((group) =>
                    <NavGroup key={group.key} group={group} currentUser={currentUser}
                              currentSelection={currentSelection} cpBaseUrl={cpBaseUrl}/>
                )}

                <li className="portal-menu-item">
                    <a href={`https://providers${linkPrefix()}.catalight.org/announcements/`}
                       className="portal-nav-hover">
                        <i className="fa-solid fa-bullhorn"/>
                        Announcements
                    </a>
                </li>
                <li className="portal-menu-item">
                    <a href={`https://providers${linkPrefix()}.catalight.org/events/`} className="portal-nav-hover">
                        <i className="fa-regular fa-calendar"/>
                        Meetings &amp; Events
                    </a>
                </li>

                <li className="portal-menu-item">
                    <a href={`https://providers${linkPrefix()}.catalight.org/filing-cabinet/provider-manual/`}
                       className="portal-nav-hover">
                        <i className="fa-regular fa-folder-open"/>
                        Filing Cabinet
                    </a>
                </li>
                <li className="portal-menu-item">
                    <a href={`https://providers${linkPrefix()}.catalight.org/support/provider-portal-training/`}
                       className="portal-nav-hover">
                        <i className="fa-solid fa-gears"/>
                        Support
                    </a>
                </li>
            </ul>

            <hr className='portal-layout-navbar-divider'/>

            <ul className='leading-4 portal-top-nav portal-nav' style={{'flexGrow': 1}}>
                <li className="portal-menu-item">
                    <a href={`https://www.catalight.org/news`} className="portal-nav-hover" target={'_blank'}>
                        <i className="fa-regular fa-newspaper"/>
                        Catalight News
                    </a>
                </li>

                <li className="portal-menu-item portal-version">
                    <span className="pl-2 cursor-not-allowed">
                        &nbsp;{feVersion}
                    </span>
                </li>
            </ul>

            {eumAccess &&
                <ul className='leading-4 portal-top-nav portal-nav'>
                    <li className={currentSelection === 'User Management' ? "portal-layout-navbar-cp" : ''}>
                        <a href={umApplicationDomainUrl + eumApplicationRoot} className="portal-nav-hover pl-2">
                            <i className="fa-solid fa-user-group"></i>
                            User Management
                        </a>
                    </li>
                </ul>
            }
        </Pane>
    )
});
